import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment/moment';
import {
  Alert,
  Card,
  CardContent,
  Link,
  IconButton,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import { getRepairOrderPrintHtml } from './printRepairOrder';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import PackageModal from './packageModal';
import PackageDetailsModal from './packageDetailsModal';
import EditRepairOrderDetailsModal from './editRepairOrderDetailsModal';
import { CloseIcon } from '../../../assets/icons/icons';

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role='tabpanel' hidden={value !== index} id={`repair-order-tabpanel-${index}`} aria-labelledby={`repair-order-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const RepairOrderSearch = () => {
  const location = useLocation(); // Get the current location
  const navigate = useNavigate(); // Get the navigate function from the react-router-dom

  // Use window.location.search as fallback if location.search is empty
  const searchString = location.search || window.location.search;
  const queryParams = new URLSearchParams(searchString); // Get the query parameters from the URL
  const [repairOrder, setRepairOrder] = useState(queryParams.get('repair-order') || '');
  const [qrCodeRequest, setQrCodeRequest] = useState(queryParams.get('qrCode') === 'true'); // Convert to boolean
  const [result, setResult] = useState(null);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [isPackageEdit, setIsPackageEdit] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditDetailsModalOpen, setIsEditDetailsModalOpen] = useState(false);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Searching for Device Information - Define early so useEffect can use it
  const handleSearch = useCallback(async (repairOrder, type) => {
    if (!repairOrder) {
      setSnackbarMessage('Please enter a Repair Order');
      setSnackbarSeverity('warning');
      setIsSnackbarOpen(true);
      return;
    }

    try {
      const res = await axios.post(`${process.env.REACT_APP_ENDPOINT_REPORTING}/depot/repairOrder/${repairOrder}`);
      setResult(res.data);
      if (type !== 'package' && type !== 'refresh') {
        setSnackbarMessage('Repair Order Search Successful');
        setSnackbarSeverity('success');
        setIsSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbarMessage(error.response?.data?.message || 'Error fetching Repair Order. Please try again. If the problem persists, contact support.');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setResult(null);
    }
  }, []);

  // Auto-search when component mounts with repair order in URL
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const orderFromUrl = params.get('repair-order');

    // Auto-search if repair order is present in URL
    if (orderFromUrl) {
      handleSearch(orderFromUrl);
    }
  }, []); // Only run once on mount

  // Update URL when repair order changes (for manual entry only)
  useEffect(() => {
    const params = new URLSearchParams();
    if (repairOrder) {
      params.set('repair-order', repairOrder);
    }
    navigate({ search: params.toString() });
  }, [repairOrder, navigate]);

  // Clearing Search Results
  const handleClear = () => {
    setRepairOrder('');
    setResult(null);
  };

  const toTitleCase = str => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const fields = result
    ? [
        { label: 'Jurisdiction', key: 'jurisdiction' },
        { label: 'Repair Order', key: 'repairOrder' },
        { label: 'Created Date', key: 'dateTime', format: 'date' },
        { label: 'Open Case', key: 'openCase' },
        { label: 'OEM', key: 'oem' },
        { label: 'RRF Repair', key: 'rrfRepair' },
        { label: 'Case Number', key: 'caseNumber' },
        { label: 'RMA Number', key: 'rmaNumber' },
        { label: 'Comments', key: 'comments' },
        { label: 'Created By', key: 'displayName' },
      ].filter(field => {
        // Hide rrfRepair if oem is "Yes"
        if (field.key === 'rrfRepair' && result.repairOrderDetails?.oem === 'Yes') {
          return false;
        }
        // Hide oemVendor if oem is not "Yes"
        if (field.key === 'oemVendor' && result.repairOrderDetails?.oem !== 'Yes') {
          return false;
        }
        return true;
      })
    : [];

  // Add print handler function
  const handlePrint = async () => {
    if (!result) return;

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setSnackbarMessage('Print window blocked. Please allow popups for this site.');
        setSnackbarSeverity('warning');
        setIsSnackbarOpen(true);
        return;
      }

      let qrCode = await QRCode.toDataURL(`${process.env.REACT_APP_DEPOT_PATH}?repair-order=${repairOrder}&qrCode=true`, { width: 200 });

      printWindow.document.write(
        getRepairOrderPrintHtml({
          result,
          fields,
          toTitleCase,
          moment,
          qrCode,
        })
      );
      printWindow.document.close();

      // Wait for print dialog, then optionally close manually
      printWindow.focus();

      // Using matchMedia to detect print dialog close (best effort)
      const mediaQueryList = printWindow.matchMedia('print');
      mediaQueryList.addEventListener('change', e => {
        if (!e.matches) {
          // User exited print dialog (printed or canceled)
          // Optionally leave window open or close it manually
          // printWindow.close(); // use this line if want it to close
        }
      });

      // Trigger the print dialog
      printWindow.print();
    } catch (error) {
      setSnackbarMessage('Error while printing. Please try again.');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  };

  const handlePackageModalOpenClose = entry => {
    setCurrentPackage(isPackageModalOpen ? null : entry);
    setIsPackageEdit(true);
    setIsPackageModalOpen(!isPackageModalOpen);
  };

  const handleDetailModalOpenClose = entry => {
    setCurrentPackage(isDetailModalOpen ? null : entry);
    setIsDetailModalOpen(!isDetailModalOpen);
  };

  const handleCreatePackageModalOpenClose = () => {
    setIsPackageEdit(false);
    setIsPackageModalOpen(!isPackageModalOpen);
  };

  return (
    <>
      <div className='container' style={{ display: 'flex' }}>
        <div style={{ flex: 1 }}>
          <TextField
            type='text'
            value={repairOrder}
            onChange={e => setRepairOrder(e.target.value)}
            placeholder={'Enter Repair Order Number'}
            variant='outlined'
            sx={{
              fontFamily: 'BrightstarSans-Regular',
              backgroundColor: '#ffffff',
              color: '#000000',
              width: '75%',
              display: 'flex',
              justifySelf: 'center',
              marginBottom: '20px',
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment>
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            onClick={() => handleSearch(repairOrder)}
            variant='contained'
            sx={{
              fontFamily: 'BrightstarSans-Regular',
              backgroundColor: 'var(--canopus-violet)',
              display: 'flex',
              justifySelf: 'center',
              marginBottom: '50px',
              width: '25%',
            }}
          >
            {'Search'}
          </Button>
          <div>
            {result && (
              <Card variant='outlined' style={{ display: 'flex', width: '100%' }}>
                <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      onClick={() => setIsEditDetailsModalOpen(true)}
                      aria-label='Edit RMA number'
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={handlePrint} aria-label='Print repair order'>
                      <PrintIcon />
                    </IconButton>
                    <IconButton onClick={handleClear}>
                      <CloseIcon />
                    </IconButton>
                  </div>

                  {/* Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      aria-label='repair order tabs'
                      indicatorColor='primary'
                      textColor='primary'
                      sx={{ fontFamily: 'BrightstarSans-Regular' }}
                    >
                      <Tab label='Details' sx={{ fontFamily: 'BrightstarSans-Bold' }} />
                      <Tab label='Entries' sx={{ fontFamily: 'BrightstarSans-Bold' }} disabled={!result.entries || result.entries.length === 0} />
                      <Tab label='Packages' sx={{ fontFamily: 'BrightstarSans-Bold' }} disabled={!result.entries || result.entries.length === 0} />
                      <Tab label='History' sx={{ fontFamily: 'BrightstarSans-Bold' }} disabled={!result.history || result.history.length === 0} />
                    </Tabs>
                  </Box>

                  {/* Tab Panels */}
                  <TabPanel value={activeTab} index={0}>
                    {/* Info Tab Content */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '150px 1fr',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        maxWidth: '800px',
                        margin: '0 auto',
                      }}
                    >
                      {fields
                        .filter(({ key }) => key !== 'additionalEntries')
                        .map(({ label, key, format }) => (
                          <React.Fragment key={key}>
                            <div
                              style={{
                                backgroundColor: 'var(--reach-blue)',
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '8px 12px',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '14px',
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                backgroundColor: '#f5f5f5',
                                padding: '8px 12px',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '14px',
                              }}
                            >
                              {format === 'date'
                                ? result.repairOrderDetails[key]
                                  ? moment(result.repairOrderDetails[key]).format('YYYY-MM-DD')
                                  : 'N/A'
                                : result.repairOrderDetails[key]
                                  ? key === 'repairOrder'
                                    ? result.repairOrderDetails[key] // Leave repairOrder unchanged
                                    : result.repairOrderDetails[key].toString()
                                  : 'N/A'}
                            </div>
                          </React.Fragment>
                        ))}
                    </div>
                  </TabPanel>

                  <TabPanel value={activeTab} index={1}>
                    {/* Entries Tab Content */}
                    {result.entries && result.entries.length > 0 && (
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxWidth: '1200px',
                          margin: '0 auto',
                        }}
                      >
                        <Table
                          sx={{
                            minWidth: 400,
                            '& .MuiTableCell-root': {
                              padding: '8px 12px',
                              fontSize: '14px',
                            },
                          }}
                          aria-label='additional entries table'
                        >
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'var(--reach-blue)' }}>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                #
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Status
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Product
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Item Number
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Serial Number
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Total Work Time (min)
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Start Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Completion Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Parts Used
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Failure Mode
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Asset Tag
                              </TableCell>
                              {result.repairOrderDetails.rmaNumber && (
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  RMA Number
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Solution
                              </TableCell>
                              {result.repairOrderDetails.oem === 'Yes' && (
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  OEM Vendor
                                </TableCell>
                              )}
                              {result.repairOrderDetails.jurisdiction === 'RRF' && (
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Return Date
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  color: 'white',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }}
                              >
                                Comments
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {result.entries.map((entry, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  '&:nth-of-type(odd)': {
                                    backgroundColor: '#ffffff',
                                  },
                                  '&:nth-of-type(even)': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {index + 1}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.status || 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.product || 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.itemNumber || 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.serialNumber || 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.totalWorkTime || 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.startDate ? moment(entry.startDate).format('MM/DD/YYYY') : 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.completionDate ? moment(entry.completionDate).format('MM/DD/YYYY') : 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.partsUsed.length > 0 ? entry.partsUsed.join(', ') : 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.failureMode || 'N/A'}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.assetTag || 'N/A'}
                                </TableCell>
                                {result.repairOrderDetails.rmaNumber && (
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {result.repairOrderDetails.rmaNumber || 'N/A'}
                                  </TableCell>
                                )}
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.solution.length > 0 ? entry.solution.join(', ') : 'N/A'}
                                </TableCell>
                                {result.repairOrderDetails.oem === 'Yes' && (
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {entry.oemVendor || 'N/A'}
                                  </TableCell>
                                )}
                                {result.repairOrderDetails.jurisdiction === 'RRF' && (
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {entry.returnDate ? moment(entry.returnDate).format('MM/DD/YYYY') : 'N/A'}
                                  </TableCell>
                                )}
                                <TableCell
                                  sx={{
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  {entry.comments || 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </TabPanel>
                  <TabPanel value={activeTab} index={2}>
                    {/* Packages Tab Content */}
                    {result.packages && result.packages.length > 0 && (
                      <div
                        style={{
                          maxWidth: '1200px',
                          margin: '0 auto',
                        }}
                      >
                        <Button
                          variant='contained'
                          sx={{
                            backgroundColor: 'var(--canopus-violet)',
                            marginBottom: '10px',
                          }}
                          onClick={handleCreatePackageModalOpenClose}
                        >
                          Add Package
                        </Button>
                        <TableContainer component={Paper}>
                          <Table
                            sx={{
                              minWidth: 400,
                              '& .MuiTableCell-root': {
                                padding: '8px 12px',
                                fontSize: '14px',
                              },
                            }}
                            aria-label='packages table'
                          >
                            <TableHead>
                              <TableRow sx={{ backgroundColor: 'var(--reach-blue)' }}>
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Edit
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Status
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Tracking Number
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Courier
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Destination
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Date Created
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                  }}
                                >
                                  Content
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {result.packages.map((entry, index) => (
                                <TableRow
                                  key={index}
                                  sx={{
                                    '&:nth-of-type(odd)': {
                                      backgroundColor: '#ffffff',
                                    },
                                    '&:nth-of-type(even)': {
                                      backgroundColor: '#f5f5f5',
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    <IconButton onClick={() => handlePackageModalOpenClose(entry)} sx={{ alignSelf: 'flex-end' }}>
                                      <EditIcon />
                                    </IconButton>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {entry.currentStatus || 'N/A'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {entry.trackingNumber || 'N/A'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {entry.courier || 'N/A'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {entry.location || 'N/A'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {entry.dateTime ? moment(entry.dateTime).format('MM/DD/YYYY') : 'N/A'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'center',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                    }}
                                  >
                                    <Link onClick={() => handleDetailModalOpenClose(entry)} sx={{ cursor: 'pointer' }}>
                                      {'Details >'}
                                    </Link>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    )}
                    {result.packages.length < 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant='contained'
                          sx={{
                            marginRight: '10px',
                            backgroundColor: 'var(--canopus-violet)',
                          }}
                          onClick={handleCreatePackageModalOpenClose}
                        >
                          Create Package
                        </Button>
                      </Box>
                    )}
                  </TabPanel>
                  <TabPanel value={activeTab} index={3}>
                    {/* History Tab Content */}
                    <List>
                      {result.history && result.history.length > 0 ? (
                        result.history.map((item, index) => (
                          <ListItem key={index} alignItems='flex-start'>
                            <ListItemText
                              primary={item.dateTime ? `${moment(item.dateTime).local().format('MM/DD/YYYY hh:mm A')} UTC` : 'N/A'}
                              secondary={
                                <Typography
                                  sx={{
                                    display: 'inline',
                                    fontWeight: 'bold',
                                    fontFamily: 'BrightstarSans-Regular',
                                  }}
                                  component='span'
                                  variant='body2'
                                  color='text.primary'
                                >
                                  {item.displayName || 'Unknown'}
                                  {' — '}
                                  {item.historyType === 'log' ? 'Modified the Repair Order' : 'Modified a Repair Order entry' || 'N/A'}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText primary='No history available.' />
                        </ListItem>
                      )}
                    </List>
                  </TabPanel>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Snackbar open={isSnackbarOpen} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant='filled' sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {isEditDetailsModalOpen && result?.repairOrderDetails && (
        <EditRepairOrderDetailsModal
          handleRefresh={() =>
            result.repairOrderDetails.repairOrder &&
            handleSearch(result.repairOrderDetails.repairOrder, 'refresh')
          }
          isOpen={isEditDetailsModalOpen}
          handleClose={() => setIsEditDetailsModalOpen(false)}
          entry={result.repairOrderDetails}
          setIsSnackbarOpen={setIsSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
        />
      )}
      {isPackageModalOpen && (
        <PackageModal
          isOpen={isPackageModalOpen}
          handleClose={handlePackageModalOpenClose}
          repairOrder={result}
          isPackageEdit={isPackageEdit}
          handleSearch={handleSearch}
          currentPackage={currentPackage}
          setIsSnackbarOpen={setIsSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
        />
      )}
      {isDetailModalOpen && (
        <PackageDetailsModal
          isOpen={isDetailModalOpen}
          handleClose={handleDetailModalOpenClose}
          repairOrder={result}
          currentPackage={currentPackage}
        />
      )}
    </>
  );
};

export default RepairOrderSearch;
